import { AxiosError } from 'axios';

/**
 * Type guard to check if error is an AxiosError
 */
const isAxiosError = (err: unknown): err is AxiosError => {
    return typeof err === 'object' && err !== null && 'isAxiosError' in err && (err as AxiosError).isAxiosError === true;
};

/**
 * Type guard to check if value is an Error
 */
const isError = (err: unknown): err is Error => {
    return err instanceof Error;
};

/**
 * Type guard to check if value has a message property
 */
const hasMessage = (data: unknown): data is { message: string } => {
    return typeof data === 'object' && data !== null && 'message' in data && typeof (data as { message: unknown }).message === 'string';
};

/**
 * Type guard to check if value has an error property
 */
const hasError = (data: unknown): data is { error: string } => {
    return typeof data === 'object' && data !== null && 'error' in data && typeof (data as { error: unknown }).error === 'string';
};

/**
 * Type guard to check if value has an errors property (object of arrays)
 */
const hasErrors = (data: unknown): data is { errors: Record<string, unknown[]> } => {
    if (typeof data !== 'object' || data === null || !('errors' in data)) {
        return false;
    }
    const errors = (data as { errors: unknown }).errors;
    return typeof errors === 'object' && errors !== null && !Array.isArray(errors);
};

/**
 * Extract error message from unknown error type
 *
 * Priority order:
 * 1. response.data.message (string)
 * 2. response.data.error (string)
 * 3. First string in response.data.errors (if object of arrays)
 * 4. err.message (if Error instance)
 * 5. Fallback "Something went wrong"
 *
 * @param err - Unknown error (could be AxiosError, Error, or anything)
 * @returns User-friendly error message string
 */
export const getErrorMessage = (err: unknown): string => {
    // Handle AxiosError
    if (isAxiosError(err)) {
        const response = err.response;
        if (response?.data) {
            const data = response.data;

            // Priority 1: response.data.message
            if (hasMessage(data) && data.message) {
                return data.message;
            }

            // Priority 2: response.data.error
            if (hasError(data) && data.error) {
                return data.error;
            }

            // Priority 3: First string in response.data.errors (object of arrays)
            if (hasErrors(data)) {
                for (const key in data.errors) {
                    const errorValue = data.errors[key];
                    if (Array.isArray(errorValue) && errorValue.length > 0) {
                        const firstError = errorValue[0];
                        if (typeof firstError === 'string') {
                            return firstError;
                        }
                    }
                }
            }
        }
    }

    // Priority 4: err.message (if Error instance)
    if (isError(err)) {
        return err.message;
    }

    // Priority 5: Fallback
    return 'Something went wrong';
};

/**
 * Extract error response data for detailed error handling
 * Returns the full response.data object if available
 */
export const getErrorResponse = (err: unknown): unknown => {
    if (isAxiosError(err) && err.response?.data) {
        return err.response.data;
    }
    return null;
};
